import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useSettings } from '../../context/SettingsContext';
import useAutoHideHeader from '../../hooks/useAutoHideHeader';
import './Header.css';

const Header = () => {
  const { logout, isAuthenticated } = useAuth();
  const { cartItemCount } = useCart();
  const { cartEnabled } = useSettings();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Pin the bar open while the mobile menu is hanging off it.
  const { hidden, atTop } = useAutoHideHeader({ locked: mobileMenuOpen });

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // SVG Icons
  const SearchIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 19L14.65 14.65"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const UserIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0 20C0 15.5817 4.47715 12 10 12C15.5228 12 20 15.5817 20 20"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const CartIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 1H4L6.68 13.39C6.77144 13.8504 7.02191 14.264 7.38755 14.5583C7.75318 14.8526 8.2107 15.009 8.68 15H16.72C17.1893 15.009 17.6468 14.8526 18.0125 14.5583C18.3781 14.264 18.6286 13.8504 18.72 13.39L20 6H5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="19" r="1" fill="currentColor" />
      <circle cx="19" cy="19" r="1" fill="currentColor" />
    </svg>
  );

  const ShippingIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 14H17M3 14V8C3 6.89543 3.89543 6 5 6H15C16.1046 6 17 6.89543 17 8V14M3 14H2M17 14H22L19 10H17M17 14V11H19"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="16" r="2" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="18" cy="16" r="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5 14H3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );

  const headerClassName = [
    'header',
    hidden ? 'header--hidden' : '',
    atTop ? '' : 'header--floating',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className="top-banner">
        <div className="top-banner-content">
          <span className="top-banner-icon">
            <ShippingIcon />
          </span>
          <span className="top-banner-text">
            Delivery All Over Nepal | Cash on Delivery Available.
          </span>
        </div>
      </div>
      <header className={headerClassName}>
        <div className="header-container">
          {/* Logo */}
          <Link to="/" className="logo">
            <img src="/logo.jpeg" alt="Essentick" className="logo-image" />
          </Link>

          {/* Navigation */}
          <nav className={`nav ${mobileMenuOpen ? 'nav-open' : ''}`}>
            <div className="nav-links">
              <Link
                to="/products?category=new"
                className="nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                New In
              </Link>
              <Link
                to="/products?category=watches"
                className="nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                Watches
              </Link>
              <Link
                to="/support"
                className="nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                Help & Support
              </Link>
            </div>

            {/* Action Icons */}
            <div className="header-actions">
              <Link to="/search" className="icon-button" aria-label="Search">
                <SearchIcon />
              </Link>
              {isAuthenticated ? (
                <Link
                  to="/profile"
                  className="icon-button"
                  aria-label="Profile"
                >
                  <UserIcon />
                </Link>
              ) : (
                <Link to="/login" className="icon-button" aria-label="Login">
                  <UserIcon />
                </Link>
              )}
              {/* No cart icon while the shop takes orders on WhatsApp. */}
              {cartEnabled &&
                (isAuthenticated ? (
                  <Link
                    to="/cart"
                    className="icon-button cart-button"
                    aria-label="Cart"
                  >
                    <CartIcon />
                    {cartItemCount > 0 && (
                      <span className="cart-badge">{cartItemCount}</span>
                    )}
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="icon-button cart-button"
                    aria-label="Cart"
                  >
                    <CartIcon />
                  </Link>
                ))}
              {isAuthenticated && (
                <button className="nav-link logout-btn" onClick={handleLogout}>
                  Logout
                </button>
              )}
            </div>
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>
    </>
  );
};

export default Header;
